import type { Plugin, ResolveIdResult } from "rolldown";

import * as Path from "node:path";

import { fdir } from "fdir";

const isThisViteVirtualModule = (id: string): boolean =>
    id.startsWith("/@") || id.startsWith("virtual:") || id.startsWith("\0");

const isThisPath = (id: string): boolean =>
    id.startsWith(".") || id.startsWith("/");

type GetIncludeExcludeOptions = {
    include: readonly string[];
    exclude: readonly string[];
};

type PathsAndPackages = {
    paths: readonly string[];
    packages: readonly string[];
};

type GetIncludeExcludeResult = {
    include: PathsAndPackages;
    exclude: PathsAndPackages;
};

const managePackagesAndPaths = (
    packagesOrPaths: readonly string[],
): PathsAndPackages => {
    const paths: string[] = [];
    const packages: string[] = [];

    for (const packageOrPath of packagesOrPaths) {
        const isPath: boolean = isThisPath(packageOrPath);

        if (!isPath) {
            packages.push(packageOrPath);
            continue;
        }

        const foundPaths: string[] = new fdir()
            .withFullPaths()
            .crawl(packageOrPath)
            .sync();

        paths.push(...foundPaths);
    }

    return {
        paths,
        packages,
    };
};

const getIncludeAndExclude = (
    options: GetIncludeExcludeOptions,
): GetIncludeExcludeResult => {
    const include: PathsAndPackages = managePackagesAndPaths(options.include);
    const exclude: PathsAndPackages = managePackagesAndPaths(options.exclude);

    return {
        include,
        exclude,
    };
};

const isPackage = (id: string): boolean => /^[^./]/.test(id);

const isJsFile = (id: string): boolean => {
    // check if the file have extension
    const hasExtension: boolean = Path.extname(id) !== "";

    // treat file without extension as js
    if (!hasExtension) return true;

    return (
        id.endsWith(".ts") ||
        id.endsWith(".tsx") ||
        id.endsWith(".js") ||
        id.endsWith(".jsx") ||
        id.endsWith(".cjs") ||
        id.endsWith(".mjs") ||
        id.endsWith(".cts") ||
        id.endsWith(".mts")
    );
};

type ExternalResolverOptions = {
    packageName: string;
    file: string;
    exclude: readonly string[];
    include: readonly string[];
};

const externalResolver = (options: ExternalResolverOptions): Plugin => {
    const normalizeTarget: string = Path.resolve(options.file);
    const { exclude, include } = getIncludeAndExclude(options);

    return {
        name: "@ammolite/transpiler/external-resolver",
        resolveId: (id: string): ResolveIdResult => {
            const normalizeSource: string = Path.resolve(id);

            /**
             * Check if the source is the same as the target
             *
             * This is to avoid the following error on Windows:
             *
             * [UNRESOLVED_ENTRY] Error: Entry module "src/app.tsx" cannot be external.
             */
            if (normalizeSource === normalizeTarget) {
                return void 0;
            }

            // CSS-in-JS package
            if (id === options.packageName) {
                return {
                    id,
                    external: true,
                };
            }

            // vite virtual modules
            if (isThisViteVirtualModule(id)) {
                return {
                    id,
                    external: true,
                };
            }

            // path
            if (isThisPath(id)) {
                if (exclude.paths.includes(id)) {
                    return {
                        id,
                        external: true,
                    };
                }

                if (include.paths.includes(id)) {
                    return void 0;
                }
            }

            // package
            else {
                for (const pkg of exclude.packages) {
                    if (id.startsWith(pkg)) {
                        return {
                            id,
                            external: true,
                        };
                    }
                }

                for (const pkg of include.packages) {
                    if (id.startsWith(pkg)) {
                        return void 0;
                    }
                }
            }

            // exclude packages
            if (isPackage(id)) {
                return {
                    id,
                    external: true,
                };
            }

            // bundle JS files
            if (isJsFile(id)) {
                return void 0;
            }

            // unknown file
            return {
                id,
                external: true,
            };
        },
    };
};

export type { ExternalResolverOptions };
export { externalResolver };
